import {createAction, ActionType} from '@packages/utilities/create-action';
import type {DesignerActionFromCreator} from '@packages/systems/designer/flux';
import {Expression} from '@packages/systems/wfdl-ast';
import {WebflowPlaybookId} from '@intellimize/dash-shared';
import {
  RAW_SITE_DATA_RECEIVED,
  OPTIMIZATION_MODAL_UPDATED,
  __DEPRECATED__BaseAction,
  SITE_PUBLISHED,
  PAGE_SWITCHED,
  REMOVE_OPTIMIZATION_WRAPPERS_ENQUEUE_STARTED,
  REMOVE_OPTIMIZATION_WRAPPERS_FAILED,
  REMOVE_OPTIMIZATION_WRAPPERS_SUCCEEDED,
  APPLY_OPTIMIZATION_WRAPPERS_ENQUEUE_STARTED,
  APPLY_OPTIMIZATION_WRAPPERS_SUCCEEDED,
  APPLY_OPTIMIZATION_WRAPPERS_FAILED,
  PUBLISH_DROPDOWN_TOGGLED,
  SITE_PUBLISHED_BEGIN,
} from '@packages/systems/designer/actions';
import {
  MetricOptimizationJson,
  OptimizationState,
} from '@packages/systems/optimization/types';
import {generateGuid} from '@packages/utilities/guid';
import type {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {Qualified} from '@packages/systems/wfdl-Qualified';
import {ExpressionEditorActionPayload} from '@packages/systems/ExpressionEditor-utils';
import {DesignerWorkflowModeChangeCompletedAction} from '@packages/systems/designer/workflows';

export const CURRENT_VARIATION_CHANGED = 'CURRENT_VARIATION_CHANGED';
export const CURRENT_METRIC_CHANGED = 'CURRENT_METRIC_CHANGED' as const;
export const CURRENT_METRIC_READY_TO_RENDER_OUTLINES =
  'CURRENT_METRIC_READY_TO_RENDER_OUTLINES' as const;

export const currentVariationChange = createAction<
  typeof CURRENT_VARIATION_CHANGED,
  {
    variationId: string | null;
    experienceId?: string | null;
    isBaseVariation?: boolean;
    isRedirectVariation?: boolean;
  }
>(CURRENT_VARIATION_CHANGED);

export const OPTIMIZATION_CURRENT_VARIATION_STYLE_REMOVED =
  'OPTIMIZATION_CURRENT_VARIATION_STYLE_REMOVED' as const;

export const optimizationCurrentVariationStyleRemoved = createAction<
  typeof OPTIMIZATION_CURRENT_VARIATION_STYLE_REMOVED,
  {styleBlockId: string; variantKeys: string[]}
>(OPTIMIZATION_CURRENT_VARIATION_STYLE_REMOVED);

export type OptimizationCurrentVariationStyleRemovedAction =
  DesignerActionFromCreator<typeof optimizationCurrentVariationStyleRemoved>;

export type CurrentVariationChangeAction = DesignerActionFromCreator<
  typeof currentVariationChange
>;

export const currentMetricChanged = createAction<
  typeof CURRENT_METRIC_CHANGED,
  {
    metric: MetricOptimizationJson | null;
    source?: string;
  }
>(CURRENT_METRIC_CHANGED);

export type CurrentMetricChangedAction = DesignerActionFromCreator<
  typeof currentMetricChanged
>;

export const currentMetricReadyToRenderOutlines = createAction<
  typeof CURRENT_METRIC_READY_TO_RENDER_OUTLINES
>(CURRENT_METRIC_READY_TO_RENDER_OUTLINES);

export type CurrentMetricReadyToRenderOutlinesAction =
  DesignerActionFromCreator<typeof currentMetricReadyToRenderOutlines>;

export const SET_SHOW_OPTIMIZATION_CREATION_TOOLTIP =
  'SET_SHOW_OPTIMIZATION_CREATION_TOOLTIP';

export const setShowOptimizationCreationTooltip = createAction<
  typeof SET_SHOW_OPTIMIZATION_CREATION_TOOLTIP,
  boolean
>(SET_SHOW_OPTIMIZATION_CREATION_TOOLTIP);

export type SetShowOptimizationCreationTooltipAction =
  DesignerActionFromCreator<typeof setShowOptimizationCreationTooltip>;

export const ELEMENT_WRAPPED_IN_VARIATION = 'ELEMENT_WRAPPED_IN_VARIATION';
export const EVENT_IDS_CHANGED_FOR_GOAL = 'EVENT_IDS_CHANGED_FOR_GOAL';

const _elementWrappedInVariation = createAction<
  typeof ELEMENT_WRAPPED_IN_VARIATION,
  {
    elementIdToBeWrapped: string;
    elementIdForNewExperienceAtom: string;
    elementIdForNewVariationAtom: string;
    elementIdForNewVariationElement: string;
  }
>(ELEMENT_WRAPPED_IN_VARIATION);

export const elementWrappedInVariation = ({
  elementIdToBeWrapped,
}: {
  elementIdToBeWrapped: string;
}) =>
  _elementWrappedInVariation({
    elementIdToBeWrapped,
    elementIdForNewExperienceAtom: generateGuid(),
    elementIdForNewVariationAtom: generateGuid(),
    elementIdForNewVariationElement: generateGuid(),
  });

export type WrapElementInVariationAction = DesignerActionFromCreator<
  typeof elementWrappedInVariation
>;

export const TREATMENT_ELEMENT_DELETED = 'TREATMENT_ELEMENT_DELETED';

export const treatmentElementDeleted = createAction<
  typeof TREATMENT_ELEMENT_DELETED,
  {fromElementId: string}
>(TREATMENT_ELEMENT_DELETED);

export type TreatmentElementDeletedAction = DesignerActionFromCreator<
  typeof treatmentElementDeleted
>;

export type EventIdsChangedForGoalPayload = {
  eventId: string;
  elementId: string;
  nativeIdPath: NativeId[];
  rootComponentType?: Qualified<string>;
  componentInstanceId: string | null;
  componentTargetType: 'all' | 'instance' | null;
  showOptimizationWarning: boolean;
};

export const eventIdsChangedForGoal = createAction<
  typeof EVENT_IDS_CHANGED_FOR_GOAL,
  EventIdsChangedForGoalPayload
>(EVENT_IDS_CHANGED_FOR_GOAL);

export type UpdateCurrentModal = {
  type: typeof OPTIMIZATION_MODAL_UPDATED;
  modal: OptimizationState['currentModal'];
};

export const INTELLIMIZE_CUSTOMER_PROVISIONED =
  'INTELLIMIZE_CUSTOMER_PROVISIONED';

export const intellimizeCustomerProvisioned = createAction<
  typeof INTELLIMIZE_CUSTOMER_PROVISIONED,
  {customerId: string}
>(INTELLIMIZE_CUSTOMER_PROVISIONED);

export type IntellimizeCustomerProvisionedAction = DesignerActionFromCreator<
  typeof intellimizeCustomerProvisioned
>;

export const DATA_COLLECTION_ENABLED_CHANGE_STARTED =
  'DATA_COLLECTION_ENABLED_CHANGED';
export const DATA_COLLECTION_ENABLED_CHANGE_SUCCEEDED =
  'DATA_COLLECTION_ENABLED_CHANGE_SUCCESS';
export const DATA_COLLECTION_ENABLED_CHANGE_ERRORED =
  'DATA_COLLECTION_ENABLED_CHANGE_FAILED';

export const dataCollectionEnabledChangeStarted = createAction<
  typeof DATA_COLLECTION_ENABLED_CHANGE_STARTED,
  {dataCollectionEnabled: boolean}
>(DATA_COLLECTION_ENABLED_CHANGE_STARTED);

export type DataCollectionEnabledChangeStarted = DesignerActionFromCreator<
  typeof dataCollectionEnabledChangeStarted
>;

export const dataCollectionEnabledChangeSucceeded = createAction<
  typeof DATA_COLLECTION_ENABLED_CHANGE_SUCCEEDED
>(DATA_COLLECTION_ENABLED_CHANGE_SUCCEEDED);

export type DataCollecitonEnabledChangeSucceeded = DesignerActionFromCreator<
  typeof dataCollectionEnabledChangeSucceeded
>;

export const dataCollectionEnabledChangeErrored = createAction<
  typeof DATA_COLLECTION_ENABLED_CHANGE_ERRORED
>(DATA_COLLECTION_ENABLED_CHANGE_ERRORED);

export type DataCollectionEnabledChangeErrored = DesignerActionFromCreator<
  typeof dataCollectionEnabledChangeErrored
>;

export const DATA_COLLECTION_TYPE_CHANGE_STARTED =
  'DATA_COLLECTION_TYPE_CHANGED';
export const DATA_COLLECTION_TYPE_CHANGE_SUCCEEDED =
  'DATA_COLLECTION_TYPE_CHANGE_SUCCESS';
export const DATA_COLLECTION_TYPE_CHANGE_ERRORED =
  'DATA_COLLECTION_TYPE_CHANGE_FAILED';

export const dataCollectionTypeChangeStarted = createAction<
  typeof DATA_COLLECTION_TYPE_CHANGE_STARTED,
  {
    dataCollectionType: OptimizationState['dataCollectionType'];
    previousDataCollectionType: OptimizationState['dataCollectionType'];
  }
>(DATA_COLLECTION_TYPE_CHANGE_STARTED);

export type DataCollectionTypeChangeStarted = DesignerActionFromCreator<
  typeof dataCollectionTypeChangeStarted
>;

export const dataCollectionTypeChangeSucceeded = createAction<
  typeof DATA_COLLECTION_TYPE_CHANGE_SUCCEEDED
>(DATA_COLLECTION_TYPE_CHANGE_SUCCEEDED);

export type DataCollectionTypeChangeSucceeded = DesignerActionFromCreator<
  typeof dataCollectionTypeChangeSucceeded
>;

export const dataCollectionTypeChangeErrored = createAction<
  typeof DATA_COLLECTION_TYPE_CHANGE_ERRORED
>(DATA_COLLECTION_TYPE_CHANGE_ERRORED);

export type DataCollectionTypeChangeErrored = DesignerActionFromCreator<
  typeof dataCollectionTypeChangeErrored
>;

export const ANTI_FLICKER_TIMEOUT_CHANGE_STARTED =
  'ANTI_FLICKER_TIMEOUT_CHANGED';
export const ANTI_FLICKER_TIMEOUT_CHANGE_SUCCEEDED =
  'ANTI_FLICKER_TIMEOUT_CHANGE_SUCCESS';
export const ANTI_FLICKER_TIMEOUT_CHANGE_ERRORED =
  'ANTI_FLICKER_TIMEOUT_CHANGE_FAILED';

export const antiFlickerTimeoutChangeStarted = createAction<
  typeof ANTI_FLICKER_TIMEOUT_CHANGE_STARTED,
  {antiFlickerTimeout: number | null}
>(ANTI_FLICKER_TIMEOUT_CHANGE_STARTED);

export type AntiFlickerTimeoutChangeStarted = DesignerActionFromCreator<
  typeof antiFlickerTimeoutChangeStarted
>;

export const antiFlickerTimeoutChangeSucceeded = createAction<
  typeof ANTI_FLICKER_TIMEOUT_CHANGE_SUCCEEDED
>(ANTI_FLICKER_TIMEOUT_CHANGE_SUCCEEDED);

export type AntiFlickerTimeoutChangeSucceeded = DesignerActionFromCreator<
  typeof antiFlickerTimeoutChangeSucceeded
>;

export const antiFlickerTimeoutChangeErrored = createAction<
  typeof ANTI_FLICKER_TIMEOUT_CHANGE_ERRORED
>(ANTI_FLICKER_TIMEOUT_CHANGE_ERRORED);

export type AntiFlickerTimeoutChangeErrored = DesignerActionFromCreator<
  typeof antiFlickerTimeoutChangeErrored
>;

export const OPTIMIZATION_JSON_INVALIDATED = 'OPTIMIZATION_JSON_INVALIDATED';

export const optimizationJsonInvalidated = createAction<
  typeof OPTIMIZATION_JSON_INVALIDATED
>(OPTIMIZATION_JSON_INVALIDATED);

export type OptimizationJsonInvalidatedAction = ActionType<
  typeof optimizationJsonInvalidated
>;

export const OPTIMIZATION_JSON_INVALIDATED_REMOTELY =
  'OPTIMIZATION_JSON_INVALIDATED_REMOTELY';

export const optimizationJsonInvalidatedRemotely = createAction<
  typeof OPTIMIZATION_JSON_INVALIDATED_REMOTELY
>(OPTIMIZATION_JSON_INVALIDATED_REMOTELY);

export const SET_IS_EXPERIENCE_POPOVER_OPEN = 'SET_IS_EXPERIENCE_POPOVER_OPEN';

export const setIsExperiencePopoverOpen = createAction<
  typeof SET_IS_EXPERIENCE_POPOVER_OPEN,
  boolean
>(SET_IS_EXPERIENCE_POPOVER_OPEN);

export type SetIsExperiencePopoverOpenAction = DesignerActionFromCreator<
  typeof setIsExperiencePopoverOpen
>;

export const SET_AI_SUGGESTION_CHAT_OPEN = 'SET_AI_SUGGESTION_CHAT_OPEN';

export const setAiSuggestionChatOpen = createAction<
  typeof SET_AI_SUGGESTION_CHAT_OPEN,
  boolean
>(SET_AI_SUGGESTION_CHAT_OPEN);

export const SET_OPTIMIZATION_CHANGED_POPOVER =
  'SET_OPTIMIZATION_CHANGED_POPOVER';

export const setOptimizationChangedPopoverIsOpen = createAction<
  typeof SET_OPTIMIZATION_CHANGED_POPOVER,
  boolean
>(SET_OPTIMIZATION_CHANGED_POPOVER);

export type SetOptimizationChangedPopoverIsOpenAction =
  DesignerActionFromCreator<typeof setOptimizationChangedPopoverIsOpen>;

export const ELEMENT_CHILDREN_CHANGED = 'ELEMENT_CHILDREN_CHANGED' as const;

export const elementChildrenChanged = createAction<
  typeof ELEMENT_CHILDREN_CHANGED,
  {
    id: string;
    data: Expression;
    children: Expression;
  }
>(ELEMENT_CHILDREN_CHANGED);

export const INSTANCE_PROP_CHANGED = 'INSTANCE_PROP_CHANGED' as const;

export const instancePropChanged = createAction<
  typeof INSTANCE_PROP_CHANGED,
  {
    id: string;
    initialId: string;
    action: ExpressionEditorActionPayload;
  }
>(INSTANCE_PROP_CHANGED);

export type SetAiSuggestionChatOpenAction = DesignerActionFromCreator<
  typeof setAiSuggestionChatOpen
>;

export type OptimizationJsonInvalidatedRemotelyAction =
  DesignerActionFromCreator<typeof optimizationJsonInvalidatedRemotely>;

type SitePublishedAction = __DEPRECATED__BaseAction<
  typeof SITE_PUBLISHED,
  {
    data: {
      site: {
        intellimizeDataCollectionEnabled: OptimizationState['dataCollectionEnabled'];
        intellimizeDataCollectionType: OptimizationState['dataCollectionType'];
        publishedIntellimizeDataCollectionEnabled: OptimizationState['dataCollectionEnabled'];
        publishedIntellimizeDataCollectionType: OptimizationState['dataCollectionType'];
        intellimizeAntiFlickerTimeout?: number | null;
        publishedIntellimizeAntiFlickerTimeout?: number | null;
      };
    };
  }
>;

type PageSwitchedAction = {
  type: typeof PAGE_SWITCHED;
  page: PageRecordType;
};

export type PreventedRemovalReason =
  | 'IS_OPTIMIZATION_OR_BASE_ELEMENT'
  | 'IS_TREATMENT_ELEMENT'
  | 'IS_NOT_OPTIMIZED_ELEMENT';

export const ELEMENT_REMOVAL_PREVENTED_IN_OPTIMIZATION =
  'ELEMENT_REMOVAL_PREVENTED_IN_OPTIMIZATION';

export const elementRemovalPreventedInOptimization = createAction<
  typeof ELEMENT_REMOVAL_PREVENTED_IN_OPTIMIZATION,
  {
    reason: PreventedRemovalReason;
  }
>(ELEMENT_REMOVAL_PREVENTED_IN_OPTIMIZATION);

type ElementRemovalPreventedInOptimizationAction = DesignerActionFromCreator<
  typeof elementRemovalPreventedInOptimization
>;

type PublishDropdownToggledAction = {
  type: typeof PUBLISH_DROPDOWN_TOGGLED;
  state: {
    UiStore: {publishDropdownOpen: boolean};
  };
};

type SitePublishedBeginAction = {
  type: typeof SITE_PUBLISHED_BEGIN;
};

export type OptimizationAction =
  | CurrentVariationChangeAction
  | RawSiteDataReceivedAction
  | IntellimizeCustomerProvisionedAction
  | UpdateCurrentModal
  | DataCollectionEnabledChangeStarted
  | DataCollecitonEnabledChangeSucceeded
  | DataCollectionEnabledChangeErrored
  | DataCollectionTypeChangeStarted
  | DataCollectionTypeChangeSucceeded
  | DataCollectionTypeChangeErrored
  | AntiFlickerTimeoutChangeStarted
  | AntiFlickerTimeoutChangeSucceeded
  | AntiFlickerTimeoutChangeErrored
  | CurrentMetricChangedAction
  | SetShowOptimizationCreationTooltipAction
  | SitePublishedAction
  | PageSwitchedAction
  | CurrentMetricReadyToRenderOutlinesAction
  | ElementRemovalPreventedInOptimizationAction
  | SetAiSuggestionChatOpenAction
  | SetIsExperiencePopoverOpenAction
  | SetOptimizationChangedPopoverIsOpenAction
  | DesignerWorkflowModeChangeCompletedAction
  | ApplyOptimizationWrappersStartedAction
  | ApplyOptimizationWrappersSucceededAction
  | ApplyOptimizationWrappersFailedAction
  | SetWrapperPublishingOptimizationNameAction
  | PublishDropdownToggledAction
  | SitePublishedBeginAction
  | SetShouldPublishApplyVariationAction
  | SetCurrentPlaybookIdAction
  | SetPlaybookCustomPromptAction;

export type RawSiteDataReceivedAction = {
  type: typeof RAW_SITE_DATA_RECEIVED;
  siteData: {
    intellimizeCustomerId: string | null;
    intellimizeDataCollectionEnabled: OptimizationState['dataCollectionEnabled'];
    intellimizeDataCollectionType: OptimizationState['dataCollectionType'];
    publishedIntellimizeDataCollectionEnabled: OptimizationState['dataCollectionEnabled'];
    publishedIntellimizeDataCollectionType: OptimizationState['dataCollectionType'];
    intellimizeAntiFlickerTimeout?: number | null;
    publishedIntellimizeAntiFlickerTimeout?: number | null;
    [key: string]: unknown;
  };
  state: unknown;
};

export const removeOptimizationWrappersStarted = createAction<
  typeof REMOVE_OPTIMIZATION_WRAPPERS_ENQUEUE_STARTED,
  {
    experienceId: string;
    variationIds: string[];
    hasSymbolUpdates?: boolean;
    isArchive?: boolean;
  }
>(REMOVE_OPTIMIZATION_WRAPPERS_ENQUEUE_STARTED);

export type RemoveOptimizationWrappersStartedAction = DesignerActionFromCreator<
  typeof removeOptimizationWrappersStarted
>;

export const removeOptimizationSucceeded = createAction<
  typeof REMOVE_OPTIMIZATION_WRAPPERS_SUCCEEDED,
  {isArchive?: boolean}
>(REMOVE_OPTIMIZATION_WRAPPERS_SUCCEEDED);

export type RemoveOptimizationSucceededAction = DesignerActionFromCreator<
  typeof removeOptimizationSucceeded
>;

export const removeOptimizationFailed = createAction<
  typeof REMOVE_OPTIMIZATION_WRAPPERS_FAILED,
  {isArchive?: boolean}
>(REMOVE_OPTIMIZATION_WRAPPERS_FAILED);

export type RemoveOptimizationFailedAction = DesignerActionFromCreator<
  typeof removeOptimizationFailed
>;

export const applyOptimizationWrappersStarted = createAction<
  typeof APPLY_OPTIMIZATION_WRAPPERS_ENQUEUE_STARTED,
  {
    experienceId: string;
    variationId: string;
    hasSymbolUpdates?: boolean;
    isArchiving?: boolean;
  }
>(APPLY_OPTIMIZATION_WRAPPERS_ENQUEUE_STARTED);

export type ApplyOptimizationWrappersStartedAction = DesignerActionFromCreator<
  typeof applyOptimizationWrappersStarted
>;

export const applyOptimizationWrappersSucceeded = createAction<
  typeof APPLY_OPTIMIZATION_WRAPPERS_SUCCEEDED
>(APPLY_OPTIMIZATION_WRAPPERS_SUCCEEDED);

export type ApplyOptimizationWrappersSucceededAction =
  DesignerActionFromCreator<typeof applyOptimizationWrappersSucceeded>;

export const applyOptimizationWrappersFailed = createAction<
  typeof APPLY_OPTIMIZATION_WRAPPERS_FAILED
>(APPLY_OPTIMIZATION_WRAPPERS_FAILED);

export type ApplyOptimizationWrappersFailedAction = DesignerActionFromCreator<
  typeof applyOptimizationWrappersFailed
>;

export const SET_WRAPPER_PUBLISHING_OPTIMIZATION_NAME =
  'SET_WRAPPER_PUBLISHING_OPTIMIZATION_NAME';

export const setWrapperPublishingOptimizationName = createAction<
  typeof SET_WRAPPER_PUBLISHING_OPTIMIZATION_NAME,
  string | null
>(SET_WRAPPER_PUBLISHING_OPTIMIZATION_NAME);

export type SetWrapperPublishingOptimizationNameAction =
  DesignerActionFromCreator<typeof setWrapperPublishingOptimizationName>;

export const SET_SHOULD_PUBLISH_APPLY_VARIATION =
  'SET_SHOULD_PUBLISH_APPLY_VARIATION';

export const setShouldPublishApplyVariation = createAction<
  typeof SET_SHOULD_PUBLISH_APPLY_VARIATION,
  boolean
>(SET_SHOULD_PUBLISH_APPLY_VARIATION);

export type SetShouldPublishApplyVariationAction = ActionType<
  typeof setShouldPublishApplyVariation
>;

export const SET_CURRENT_PLAYBOOK_ID = 'SET_CURRENT_PLAYBOOK_ID';

export const setCurrentPlaybookId = createAction<
  typeof SET_CURRENT_PLAYBOOK_ID,
  WebflowPlaybookId | null
>(SET_CURRENT_PLAYBOOK_ID);

export type SetCurrentPlaybookIdAction = DesignerActionFromCreator<
  typeof setCurrentPlaybookId
>;

export const SET_PLAYBOOK_CUSTOM_PROMPT = 'SET_PLAYBOOK_CUSTOM_PROMPT';

export const setPlaybookCustomPrompt = createAction<
  typeof SET_PLAYBOOK_CUSTOM_PROMPT,
  string | null
>(SET_PLAYBOOK_CUSTOM_PROMPT);

export type SetPlaybookCustomPromptAction = DesignerActionFromCreator<
  typeof setPlaybookCustomPrompt
>;
